import type { RouteProps } from "../../types";
import type { ReportData, ReportFilters, TagItem } from "../../types/documents";

import { Box, Stack } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { useState } from "react";
import { BreadcrumbHeader } from "../../components/general/BreadcrumbHeader";
import { ClientsWithNewslettersExport } from "../../components/reports/ClientsWithNewslettersExport";
import { ClientsWithSurveysExport } from "../../components/reports/ClientsWithSurveysExport";
import { CSVExportButton } from "../../components/reports/ExportButton";
import { ReportFilterBar } from "../../components/reports/ReportDateFilter";
import { useReadQuery } from "../../hooks/general/useReadQuery";
import {
  reportFields,
  reportStrings,
  tagGroupMapping,
} from "../../resources/strings/reports";
import { api } from "../../services/reports.service";
import { api as tagsApi } from "../../services/tags.service";
import { TableContainer } from "../general/TableContainer";
import { tableColumns } from "./tableColumns";

export const ReportsContainer = (props: RouteProps) => {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [selectedFields, setSelectedFields] = useState<
    (keyof typeof reportFields)[]
  >(Object.keys(reportFields) as (keyof typeof reportFields)[]);

  const reportQueries = useQueries({
    queries: selectedFields.map((propertyName) => ({
      queryKey: [`report-${propertyName}`, 0, 20, { propertyName, ...filters }],
      queryFn: () => api.query({ query: { propertyName, ...filters } }),
    })),
  });

  const tagQueries = useQueries({
    queries: selectedFields.map((name) => {
      const tagGroup = tagGroupMapping[name] ?? name;
      return {
        queryKey: [`tags/${tagGroup}`, tagGroup, null],
        queryFn: () => tagsApi.read(tagGroup),
      };
    }),
  });

  const data = selectedFields.map((propertyName, index) => ({
    data: reportQueries[index].data?.items ?? [],
    tags: tagQueries[index].data?.item?.tags ?? [],
    header: reportFields[propertyName],
  }));

  return (
    <>
      <BreadcrumbHeader
        crumbs={[{ text: reportStrings.header.report }]}
        children={
          <>
            <CSVExportButton
              tableDefinition={tableColumns}
              rawData={data}
              filters={filters}
            />
            <ClientsWithSurveysExport />
            <ClientsWithNewslettersExport />
          </>
        }
      />

      <Box padding={2}>
        <ReportFilterBar
          handleFilterChange={(key, val) =>
            setFilters((old) => ({ ...old, [key]: val }))
          }
          clearFilters={() => setFilters({})}
          filters={filters}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
        />
      </Box>

      <Stack spacing={5}>
        {selectedFields.map((field) => (
          <ReportTable
            key={field}
            propertyName={field}
            header={reportFields[field]}
            dateRange={filters}
            {...props}
          />
        ))}
      </Stack>
    </>
  );
};

interface TableProps extends RouteProps {
  propertyName: keyof typeof reportFields;
  header: string;
  dateRange: ReportFilters;
}

const ReportTable = ({
  propertyName,
  header,
  dateRange,
  ...routeProps
}: TableProps) => {
  // We want the tag order as well, so we can't use the
  // useTagGroup hook which only returns id/text
  const tagGroup = tagGroupMapping[propertyName] ?? propertyName;
  const [{ tags }] = useReadQuery<TagItem>(
    { tagGroup: "", name: "", tags: [] },
    `tags/${tagGroup}`,
    tagsApi.read,
    tagGroup
  );

  return (
    <TableContainer<ReportData>
      {...routeProps}
      queryKey={`report-${propertyName}`}
      api={api}
      tableColumns={tableColumns(header, tags, dateRange)}
      defaultSort={[{ id: "order", desc: false }]}
      filters={{ propertyName, ...dateRange }}
      dense
      hidePagination
    />
  );
};
